import { memo, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconSquaresConnectedOutline18 } from "nucleo-ui-outline-18"

import type { AgentCapabilityGuide } from "@productlane/lib/src/agent-recommendations"
import { Button, Textarea } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { useAutosaveText } from "@/lib/use-autosave-text"
import { useZero } from "@/lib/zero"
import { connectorSetup, ENGINEERING_COPY } from "./format"

const GuideEditor = memo(
	({
		recommendationId,
		guide,
	}: {
		recommendationId: string
		guide: AgentCapabilityGuide
	}) => {
		const zero = useZero()
		const [need, setNeed] = useState(guide.need)
		const [suggestion, setSuggestion] = useState(guide.suggestion)
		const save = (next: { need: string; suggestion: string }) => {
			void zero.mutate(
				mutators.agentRecommendations.editGuide({
					id: recommendationId,
					...next,
				}),
			)
		}
		useAutosaveText({
			value: need,
			persistedValue: guide.need,
			enabled: true,
			onSave: (value) => save({ need: value, suggestion }),
		})
		useAutosaveText({
			value: suggestion,
			persistedValue: guide.suggestion,
			enabled: true,
			onSave: (value) => save({ need, suggestion: value }),
		})
		return (
			<div className="flex flex-col gap-4">
				<Textarea
					name="agent-guide-need"
					label={ENGINEERING_COPY.need}
					value={need}
					onChange={(event) => setNeed(event.target.value)}
					minRows={2}
				/>
				<Textarea
					name="agent-guide-suggestion"
					label={ENGINEERING_COPY.suggestion}
					value={suggestion}
					onChange={(event) => setSuggestion(event.target.value)}
					minRows={3}
				/>
			</div>
		)
	},
)

const ExampleQuestions = memo(({ questions }: { questions: string[] }) => {
	if (questions.length === 0) return null
	return (
		<div className="flex flex-col gap-1">
			<h4 className="text-xs font-medium text-tertiary">
				{ENGINEERING_COPY.questions}
			</h4>
			<ul className="flex list-disc flex-col gap-1 pl-5 text-secondary">
				{questions.map((question) => (
					<li key={question}>{question}</li>
				))}
			</ul>
		</div>
	)
})

export const CapabilityGuide = memo(
	({
		recommendationId,
		guide,
		editing = false,
	}: {
		recommendationId: string
		guide: AgentCapabilityGuide
		editing?: boolean
	}) => {
		const navigate = useNavigate()
		const questions = guide.exampleQuestions.filter((q) => q.trim())
		const setup = connectorSetup(guide)
		return (
			<div
				data-testid="capability-guide"
				className="flex flex-col gap-4 text-sm leading-6"
			>
				{editing ? (
					<GuideEditor recommendationId={recommendationId} guide={guide} />
				) : (
					<>
						<p>{guide.need}</p>
						<div className="flex flex-col gap-1">
							<h4 className="text-xs font-medium text-tertiary">
								{ENGINEERING_COPY.suggestion}
							</h4>
							<p className="text-secondary">{guide.suggestion}</p>
						</div>
						<div>
							<Button
								variant="outline"
								Icon={IconSquaresConnectedOutline18}
								onClick={() => void navigate({ href: setup.path })}
							>
								{setup.label}
							</Button>
						</div>
					</>
				)}
				<ExampleQuestions questions={questions} />
			</div>
		)
	},
)
